import { IGetValueArgs } from "@components/smart/FieldInfo";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import {
    EntitySetName,
    EntityTypeName,
    ICorrectiveInvoiceIssuedEntity,
    ICorrectiveInvoiceReceivedEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode } from "@odata/GeneratedEnums";
import { transformToODataString } from "@odata/OData.utils";
import { isObjectEmpty } from "@utils/general";
import i18next from "i18next";

import { Sort, ValueType } from "../../../enums";
import { ISplitPageTableDef, ITableDefTabData, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { IDefinition } from "../../PageUtils";
import { DocumentStatusLocalPath, StatusEntities } from "../../reports/CommonDefs";
import { getDocumentStatusColumnDefinition } from "../DocumentCommonDefs";
import { getDocumentStatusFilterDefinition } from "../DocumentDef";
import { correctedDocAdditionalProperties } from "./CommonCorrectiveSharedUtils";

const getWithoutCorrectiveDocumentFilter = (showOnlySimilarDocuments: boolean, args: IGetValueArgs) => {
    const filters: string[] = [
        "CorrectiveDocument eq null"
    ];
    // this is called from both form (for select) and DialogPage TableView (for pairing dialog)
    // => get the correct storage
    const storage = (args.storage as TSmartODataTableStorage).getCustomData().rootStorage ?? args.storage;
    const entity = storage.data.entity as (ICorrectiveInvoiceIssuedEntity | ICorrectiveInvoiceReceivedEntity);

    if (showOnlySimilarDocuments) {
        const firstLinkDoc = entity.CorrectedDocuments?.[0];
        const transCurrCode = firstLinkDoc?.TransactionCurrencyCode ?? firstLinkDoc?.TransactionCurrency?.Code;
        if (transCurrCode) {
            filters.push(`TransactionCurrencyCode eq '${transCurrCode}'`);
        }

        if (!isObjectEmpty(firstLinkDoc?.BusinessPartner)) {
            filters.push(`BusinessPartner/Name eq ${transformToODataString(firstLinkDoc.BusinessPartner.Name, ValueType.String)}`);
            filters.push(`BusinessPartner/LegalNumber eq ${transformToODataString(firstLinkDoc.BusinessPartner.LegalNumber, ValueType.String)}`);
        }
    }

    const filter = filters.join(" AND ");

    if (!storage.data.bindingContext.isNew()) {
        // always include the already selected documents
        return `((${filter}) OR CorrectiveDocument/Id eq ${storage.data.entity.Id})`;
    }
    return filter;
};


export const getReceivedDocsWithoutCorrectionFilter = (showOnlySimilarDocuments = true) => {
    return (args: IGetValueArgs): string => {
        return `${getWithoutCorrectiveDocumentFilter(showOnlySimilarDocuments, args)} AND DocumentTypeCode in (${[`'${DocumentTypeCode.InvoiceReceived}'`, `'${DocumentTypeCode.OtherLiability}'`].join(",")})`;
    };
};


export const getIssuedDocsWithoutCorrectionFilter = (showOnlySimilarDocuments = true) => {
    return (args: IGetValueArgs): string => {
        return `${getWithoutCorrectiveDocumentFilter(showOnlySimilarDocuments, args)} AND DocumentTypeCode in (${[`'${DocumentTypeCode.InvoiceIssued}'`, `'${DocumentTypeCode.OtherReceivable}'`].join(",")})`;
    };
};

export const getDefinition = (isReceivableDocType?: boolean): IDefinition => {
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        isValueHelp: true,
        defaultFilters: [
            "NumberOurs",
            "DocumentType",
            ...(isReceivableDocType ? ["NumberTheirs"] : []),
            "Amount",
            "BusinessPartner",
            DocumentStatusLocalPath
        ],
        filterDefinition: {
            ...withDisplayName("DocumentType"),
            ...(isReceivableDocType ? { NumberTheirs: {} } : {}),
            NumberOurs: {},
            Amount: {},
            ...withDisplayName("BusinessPartner"),
            ...getDocumentStatusFilterDefinition(StatusEntities),
            ...withDisplayName("TransactionCurrency"),
            "BusinessPartner/Street": {},
            "BusinessPartner/City": {},
            "BusinessPartner/PostalCode": {},
            "BusinessPartner/Country": {},
            "BusinessPartner/FirstName": {},
            "BusinessPartner/LastName": {},
            "BusinessPartner/Email": {},
            "BusinessPartner/PhoneNumber": {}

        }
    }];

    const tabs: ITableDefTabData[] = [{
        id: SECONDARY_FILTERS_ALL,
        title: i18next.t("Components:FilterBar.All")
    },
        {
            id: isReceivableDocType ? DocumentTypeCode.InvoiceReceived : DocumentTypeCode.InvoiceIssued,
            title: i18next.t(`Common:GlobalSearchGroups.${isReceivableDocType ? EntityTypeName.InvoiceReceived : EntityTypeName.InvoiceIssued}`)
        },
        {
            id: isReceivableDocType ? DocumentTypeCode.OtherLiability : DocumentTypeCode.OtherReceivable,
            title: i18next.t(`Common:GlobalSearchGroups.${isReceivableDocType ? EntityTypeName.OtherLiability : EntityTypeName.OtherReceivable}`)
        }
    ];

    const table: ISplitPageTableDef = {
        filterBarDef,
        preventStoreVariant: true,
        tabs,
        tabsSettings: {
            filterFieldName: "DocumentTypeCode"
        },
        id: "CorrectiveDocumentPairingSpecialTable",
        // todo get the definition from already existing document defs?
        columnDefinition: {
            NumberOurs: { id: "NumberOurs" },
            // commented lines are not included in PayingDocuments
            // DateAccountingTransaction: { id: "DateAccountingTransaction" },
            DateDue: { id: "DateDue" },
            // PaymentMethodCode: { id: "PaymentMethodCode" },
            SymbolVariable: { id: "SymbolVariable" },
            // SymbolSpecific: { id: "SymbolSpecific" },
            // SymbolConstant: { id: "SymbolConstant" },
            // "BankAccount/AccountNumber": {},
            // "BankAccount/BankCode": {},
            Amount: {},
            TransactionAmountDue: {},
            Note: {},
            ...getDocumentStatusColumnDefinition(),
            BusinessPartner: {
                id: "BusinessPartner",
                fieldSettings: {
                    displayName: "Name"
                }
            },
            "BusinessPartner/Street": {},
            "BusinessPartner/City": {},
            "BusinessPartner/PostalCode": {},
            "BusinessPartner/Country": {},
            "BusinessPartner/FirstName": {},
            "BusinessPartner/LastName": {},
            "BusinessPartner/Email": {},
            "BusinessPartner/PhoneNumber": {},
            // "AccountAssignmentSelection/AccountAssignment": {
            //     id: "AccountAssignmentSelection/AccountAssignment",
            //     fieldSettings: {
            //         displayName: "Name"
            //     }
            // },
            // ...getCommonTableColumnsDefs(),
            DocumentType: {
                fieldSettings: {
                    displayName: "Name"
                }
            },
            DateIssued: {},
            DateCreated: {},
            ...(isReceivableDocType ? { NumberTheirs: {} } : {})
        },
        columns: [
            "NumberOurs",
            DocumentStatusLocalPath,
            "DocumentType",
            ...(isReceivableDocType ? ["NumberTheirs"] : []),
            "BusinessPartner",
            "DateIssued",
            "DateDue",
            "Amount",
            "TransactionAmountDue",
            "SymbolVariable",
            "Note"
        ],
        filter: isReceivableDocType ? getReceivedDocsWithoutCorrectionFilter(false) : getIssuedDocsWithoutCorrectionFilter(false),
        initialSortBy: [{ id: "DateCreated", sort: Sort.Desc }],
        additionalProperties: correctedDocAdditionalProperties
    };

    return {
        entitySet: EntitySetName.PayingDocuments,
        table
    };
};